/**
 * <s-product-gallery> — One large product photo with a clickable thumbnail rail.
 *
 * Required markup:
 *   [data-product-gallery]            — horizontal scroll-snap track holding one slide per media.
 *   [data-media-id]                   — each slide inside the track.
 *   [data-gallery-thumb="<media id>"] — thumbnail buttons; aria-current marks the visible slide.
 *   script[data-variant-media]        — JSON map of variant id → featured media id.
 *
 * Element dataset:
 *   data-initial-media-id — media shown on load (selected variant's image).
 *
 * Variant changes: listens for `change` events on inputs/options carrying data-variant-id
 * (the same contract s-variant-picker uses) and shows that variant's assigned image.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    try {
      this.#variantMedia = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }

    this.addEventListener('click', this.#handleThumbClick, opts);
    document.addEventListener('change', this.#handleVariantChange, opts);

    const track = this.#track;
    if (track && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#handleIntersect, { root: track, threshold: 0.6 });
      for (const slide of track.querySelectorAll('[data-media-id]')) this.#observer.observe(slide);
    }

    const initialId = this.dataset.initialMediaId;
    if (initialId) this.show(initialId, { instant: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  get #track() {
    return this.querySelector('[data-product-gallery]');
  }

  show(mediaId, { instant = false } = {}) {
    const track = this.#track;
    const slide = track?.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!track || !slide) return;

    const left = this.#scrollTarget(track, slide.offsetLeft - track.offsetLeft);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left, behavior: instant || reduceMotion ? 'instant' : 'smooth' });
    this.#setCurrent(mediaId);
  }

  // offsetLeft is measured from the left edge, but RTL scrollers run from 0 down to -max.
  #scrollTarget(scroller, fromLeft) {
    const max = scroller.scrollWidth - scroller.clientWidth;
    const left = Math.min(max, Math.max(0, fromLeft));
    return getComputedStyle(scroller).direction === 'rtl' ? left - max : left;
  }

  #setCurrent(mediaId) {
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (thumb.dataset.galleryThumb === String(mediaId)) {
        thumb.setAttribute('aria-current', 'true');
        const rail = thumb.parentElement;
        if (rail && rail.scrollWidth > rail.clientWidth) {
          rail.scrollTo({ left: this.#scrollTarget(rail, thumb.offsetLeft - rail.offsetLeft - rail.clientWidth / 2 + thumb.clientWidth / 2), behavior: 'smooth' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  #handleThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;
    this.show(thumb.dataset.galleryThumb);
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#setCurrent(entry.target.dataset.mediaId);
    }
  };

  #handleVariantChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId) return;
    const mediaId = this.#variantMedia[variantId];
    if (mediaId) this.show(mediaId);
  };
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
